import React, { useState, useEffect, useCallback } from 'react';
import { ghlApi } from '../../lib/ghlApi';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../ui/sheet';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SchedulingGridDialog } from './SchedulingGridDialog';
import {
  Phone, Clock, User, Mail, Calendar, Sparkles,
  Loader2, Save, ExternalLink, Plus, CheckCircle,
  Tag, Users, Bot, CalendarDays, MapPin, X, CalendarPlus,
} from 'lucide-react';
import { cn, stripHtml } from '../../lib/utils';

const FRONT_DESK_FIELDS = [
  { key: 'contact.front_desk_team', label: 'Front Desk Team' },
  { key: 'contact.contact_status', label: 'Patient Status' },
  { key: 'contact.new_patient_status', label: 'New Patient Status' },
  { key: 'contact.conversation_summary', label: 'Patient Message' },
  { key: 'contact.appointmeent_booked_by', label: 'Assessment Apt Booked By' },
  { key: 'contact.name_of_therapist_who_did_first_consultation', label: 'Therapist (First Consultation)' },
];

const TABS = [
  { id: 'info', label: 'Patient Info', icon: User },
  { id: 'fd-team', label: 'Front Desk', icon: Users },
  { id: 'notes', label: 'Notes', icon: Mail },
  { id: 'tasks', label: 'Tasks', icon: CheckCircle },
  { id: 'appointments', label: 'Appointments', icon: CalendarDays },
];

export function PatientDetailPanel({ open, onOpenChange, call, locationId, preloadedContact }) {
  const [activeTab, setActiveTab] = useState('info');
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customFieldMap, setCustomFieldMap] = useState({});
  const [editFields, setEditFields] = useState({});
  const [fdValues, setFdValues] = useState({});
  const [fdSaving, setFdSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [crmUsers, setCrmUsers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteAgent, setNoteAgent] = useState('');
  const [noteUserId, setNoteUserId] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [taskAgent, setTaskAgent] = useState('');
  const [taskUserId, setTaskUserId] = useState('');
  const [taskSaving, setTaskSaving] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [juvonnoId, setJuvonnoId] = useState('');
  const [juvonnoPortalUrl, setJuvonnoPortalUrl] = useState('');
  const [showScheduleGrid, setShowScheduleGrid] = useState(false);

  const contactId = call?.contactId;

  // Load ALL data in parallel — no staggered delays
  const loadContact = useCallback(async () => {
    if (!contactId || !locationId) return;
    // If preloadedContact exists, set it immediately for instant display
    if (preloadedContact) {
      const c = preloadedContact;
      setContact(c);
      setEditFields({
        firstName: c.firstName || '', lastName: c.lastName || '',
        email: c.email || '', phone: c.phone || '',
        dateOfBirth: c.dateOfBirth || '', gender: c.gender || '',
        address1: c.address1 || '', city: c.city || '',
        state: c.state || '', postalCode: c.postalCode || '', source: c.source || '',
      });
    }
    setLoading(!preloadedContact);
    try {
      // Always fetch fresh contact from API to avoid stale data
      const [contactResp, cfResp, agentsResp, usersResp] = await Promise.all([
        ghlApi.getContact(contactId, locationId),
        ghlApi.getCustomFields(locationId),
        ghlApi.getCallAgents(locationId).catch(() => ({ data: { agents: [] } })),
        ghlApi.getUsers(locationId).catch(() => ({ data: { users: [] } })),
      ]);

      const c = contactResp.data?.contact || contactResp.data || {};
      setContact(c);
      setAgents(agentsResp.data?.agents || []);
      setCrmUsers(usersResp.data?.users || []);

      const fields = cfResp.data?.customFields || [];
      const fMap = {};
      for (const f of fields) { if (f.fieldKey) fMap[f.fieldKey] = f; }
      setCustomFieldMap(fMap);

      // Look up gender from custom fields
      let contactGender = c.gender || '';
      if (!contactGender) {
        const genderFieldDef = fields.find(f => {
          const key = (f.fieldKey || '').toLowerCase().trim();
          return key === 'contact.gender';
        });
        if (genderFieldDef) {
          const cfs = c.customFields || c.customField || [];
          const gf = (Array.isArray(cfs) ? cfs : []).find(cf => cf.id === genderFieldDef.id);
          contactGender = gf?.value || '';
        }
      }

      setEditFields({
        firstName: c.firstName || '', lastName: c.lastName || '',
        email: c.email || '', phone: c.phone || '',
        dateOfBirth: c.dateOfBirth || '', gender: contactGender,
        address1: c.address1 || '', city: c.city || '',
        state: c.state || '', postalCode: c.postalCode || '', source: c.source || '',
      });
      const cfs = c.customFields || c.customField || [];
      let jId = '';
      const fdVals = {};
      for (const cf of (Array.isArray(cfs) ? cfs : [])) {
        const fieldDef = fields.find(f => f.id === cf.id);
        if (fieldDef) {
          if (fieldDef.fieldKey === 'contact.juvonno_customer_id' || fieldDef.name?.toLowerCase().includes('juvonno')) jId = cf.value || '';
          for (const fd of FRONT_DESK_FIELDS) {
            if (fieldDef.fieldKey === fd.key) {
              // MULTIPLE_OPTIONS fields return arrays — join or take first value
              const rawVal = cf.value;
              fdVals[fd.key] = Array.isArray(rawVal) ? rawVal[0] || '' : (rawVal || '');
            }
          }
        }
      }
      setJuvonnoId(jId);
      setFdValues(fdVals);
    } catch { toast.error('Failed to load patient'); } finally { setLoading(false); }
  }, [contactId, locationId]);

  const loadNotes = useCallback(async () => {
    if (!contactId || !locationId) return;
    setNotesLoading(true);
    try { const r = await ghlApi.getContactNotes(locationId, contactId); setNotes(r.data?.notes || []); } catch {} finally { setNotesLoading(false); }
  }, [contactId, locationId]);

  const loadTasks = useCallback(async () => {
    if (!contactId || !locationId) return;
    setTasksLoading(true);
    try { const r = await ghlApi.getContactTasks(locationId, contactId); setTasks(r.data?.tasks || []); } catch {} finally { setTasksLoading(false); }
  }, [contactId, locationId]);

  const loadAppointments = useCallback(async () => {
    if (!contactId || !locationId) return;
    setAppointmentsLoading(true);
    try {
      const resp = await ghlApi.getContactAppointments(locationId, contactId);
      setAppointments(resp.data?.events || []);
    } catch {} finally { setAppointmentsLoading(false); }
  }, [contactId, locationId]);

  useEffect(() => {
    if (open && contactId) {
      setActiveTab('info'); setShowAddNote(false); setShowAddTask(false);
      loadContact();
      // Notes, tasks, appointments load lazily when tab is selected
      try { const loc = JSON.parse(localStorage.getItem('current_location') || '{}'); setJuvonnoPortalUrl(loc.juvonno_portal_url || ''); } catch {}
    }
  }, [open, contactId, loadContact]);

  // Lazy load: fetch data only when tab is activated
  useEffect(() => {
    if (!open || !contactId) return;
    if (activeTab === 'notes' && notes.length === 0 && !notesLoading) loadNotes();
    if (activeTab === 'tasks' && tasks.length === 0 && !tasksLoading) loadTasks();
    if (activeTab === 'appointments' && appointments.length === 0 && !appointmentsLoading) loadAppointments();
  }, [activeTab, open, contactId]);

  const handleSavePatientInfo = async () => {
    setSaving(true);
    try {
      await ghlApi.updateContact(contactId, locationId, {
        first_name: editFields.firstName, last_name: editFields.lastName,
        email: editFields.email, phone: editFields.phone,
        date_of_birth: editFields.dateOfBirth, gender: editFields.gender,
        address1: editFields.address1, city: editFields.city,
        state: editFields.state, postal_code: editFields.postalCode,
      });
      toast.success('Patient info saved');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = 'Failed to save';
      if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object') msg = detail.message || msg;
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const handleSaveFdField = async (fieldKey, value) => {
    const fieldDef = customFieldMap[fieldKey];
    if (!fieldDef) return;
    setFdSaving(true);
    try {
      await ghlApi.updateContact(contactId, locationId, { custom_fields: [{ id: fieldDef.id, field_value: value }] });
      setFdValues(prev => ({ ...prev, [fieldKey]: value }));
      toast.success('Saved');
    } catch { toast.error('Failed'); } finally { setFdSaving(false); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setNoteSaving(true);
    try {
      const body = noteAgent && noteAgent !== '__none__' ? `[${noteAgent}] ${newNote.trim()}` : newNote.trim();
      const payload = { contact_id: contactId, body };
      if (noteUserId && noteUserId !== '__none__') payload.user_id = noteUserId;
      await ghlApi.addNote(locationId, payload);
      setNewNote(''); setNoteAgent(''); setNoteUserId(''); setShowAddNote(false); loadNotes(); toast.success('Note added');
    } catch { toast.error('Failed'); } finally { setNoteSaving(false); }
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    setTaskSaving(true);
    try {
      const desc = taskAgent && taskAgent !== '__none__' ? `[Agent: ${taskAgent}] ${newTaskDesc.trim()}` : newTaskDesc.trim();
      const assignedTo = taskUserId && taskUserId !== '__none__' ? taskUserId : (contact?.assignedTo || '');
      await ghlApi.createTask(locationId, {
        contact_id: contactId, title: newTaskTitle.trim(), description: desc, assigned_to: assignedTo,
      });
      setNewTaskTitle(''); setNewTaskDesc(''); setTaskAgent(''); setTaskUserId(''); setShowAddTask(false); loadTasks(); toast.success('Task added');
    } catch { toast.error('Failed'); } finally { setTaskSaving(false); }
  };

  const patientType = call?.patientType || (juvonnoId ? 'existing' : 'new');

  // Derive last confirmed appointment date from loaded appointments
  const lastAppointmentDate = (() => {
    const confirmed = appointments
      .filter(a => a.appointmentStatus === 'confirmed' && a.startTime)
      .map(a => new Date(a.startTime))
      .filter(d => !isNaN(d.getTime()))
      .sort((a, b) => b - a);
    return confirmed.length > 0 ? confirmed[0] : null;
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="!w-[780px] !max-w-[780px] p-0 overflow-hidden flex flex-col [&>button:first-of-type]:hidden" data-testid="patient-detail-panel">
        <VisuallyHidden>
          <SheetTitle>Patient Detail — {(call?.contactName || call?.name || 'Patient')}</SheetTitle>
          <SheetDescription>Edit patient info, front desk assignment, notes, tasks and appointments.</SheetDescription>
        </VisuallyHidden>
        {/* HEADER */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white px-6 py-4 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold tracking-tight">{call?.contactName || 'Patient Details'}</h2>
              <p className="text-slate-300 text-sm flex items-center gap-1.5 mt-0.5"><Phone className="h-3.5 w-3.5" /> {call?.phone || '-'}</p>
            </div>
            <div className="flex items-center gap-2">
              {activeTab === 'info' && (
                <Button size="sm" variant="secondary" onClick={handleSavePatientInfo} disabled={saving} className="h-8 gap-1 bg-white/10 hover:bg-white/20 text-white border-0" data-testid="save-patient-info">
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
                </Button>
              )}
              <button onClick={() => onOpenChange(false)} className="text-slate-400 hover:text-white p-1"><X className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-300 mt-2">
            {call?.dateAdded && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {format(new Date(call.dateAdded), 'MMM d, h:mm a')}</span>}
            {call?.dateAdded && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formatDistanceToNow(new Date(call.dateAdded), { addSuffix: true })}</span>}
            {call?.duration > 0 && <span>{Math.floor(call.duration / 60)}m {call.duration % 60}s</span>}
            <span>{call?.direction === 'inbound' ? 'Inbound' : 'Outbound'}</span>
            {call?.answeredBy && <span className="flex items-center gap-1">{call.answeredBy === 'ai' ? <Bot className="h-3 w-3" /> : <User className="h-3 w-3" />} {call.answeredBy}</span>}
          </div>
          <div className="flex gap-1.5 mt-2">
            {call?.aiClassified && <Badge className="bg-amber-500/20 text-amber-200 border-amber-500/30 text-[10px]"><Sparkles className="h-2.5 w-2.5 mr-0.5" /> AI</Badge>}
            {call?.classification && <Badge className={cn('text-[10px] border', call.classification === 'booked' ? 'bg-green-500/20 text-green-200 border-green-500/30' : call.classification === 'not-booked' ? 'bg-red-500/20 text-red-200 border-red-500/30' : 'bg-blue-500/20 text-blue-200 border-blue-500/30')}>{call.classification}</Badge>}
          </div>
        </div>

        {/* TAB BAR */}
        <div className="bg-white border-b px-4 flex-shrink-0">
          <div className="flex gap-0.5 -mb-px">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={cn('flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
                    activeTab === tab.id ? 'border-slate-800 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'
                  )} data-testid={`pd-tab-${tab.id}`}>
                  <Icon className="h-3.5 w-3.5" /> {tab.label}
                  {tab.id === 'notes' && notes.length > 0 && <span className="bg-slate-200 text-slate-600 rounded-full px-1.5 text-[10px]">{notes.length}</span>}
                  {tab.id === 'tasks' && tasks.length > 0 && <span className="bg-slate-200 text-slate-600 rounded-full px-1.5 text-[10px]">{tasks.length}</span>}
                  {tab.id === 'appointments' && appointments.length > 0 && <span className="bg-slate-200 text-slate-600 rounded-full px-1.5 text-[10px]">{appointments.length}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto bg-slate-50">
          {loading ? (
            // Show skeleton with data from call prop immediately — no blank screen
            <div className="p-6 space-y-4 animate-pulse">
              <div className="bg-white rounded-xl border p-5 space-y-3">
                <div className="h-3 bg-slate-200 rounded w-1/3" />
                <div className="grid grid-cols-2 gap-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={`skel-a-${i}`} className="h-9 bg-slate-100 rounded-lg" />
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl border p-5">
                <div className="h-3 bg-slate-200 rounded w-1/4 mb-3" />
                <div className="grid grid-cols-2 gap-3">
                  {[...Array(4)].map((_, i) => <div key={`skel-${i}`} className="h-9 bg-slate-100 rounded-lg" />)}
                </div>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'info' && (
                <div className="p-6 space-y-5">
                  <Card title="Personal Information">
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="First Name" value={editFields.firstName} onChange={v => setEditFields(p => ({ ...p, firstName: v }))} />
                      <Field label="Last Name" value={editFields.lastName} onChange={v => setEditFields(p => ({ ...p, lastName: v }))} />
                      <Field label="Email" value={editFields.email} onChange={v => setEditFields(p => ({ ...p, email: v }))} icon={Mail} />
                      <Field label="Phone" value={editFields.phone} onChange={v => setEditFields(p => ({ ...p, phone: v }))} icon={Phone} />
                      <Field label="Date of Birth" value={editFields.dateOfBirth} onChange={v => setEditFields(p => ({ ...p, dateOfBirth: v }))} type="date" />
                      <div>
                        <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Gender</label>
                        <Select value={editFields.gender || ''} onValueChange={v => setEditFields(p => ({ ...p, gender: v === '__none__' ? '' : v }))}>
                          <SelectTrigger className="h-10" data-testid="gender-select"><SelectValue placeholder="Select gender" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">-- None --</SelectItem>
                            <SelectItem value="Male">Male</SelectItem>
                            <SelectItem value="Female">Female</SelectItem>
                            <SelectItem value="Unknown">Unknown</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-5 pt-3 mt-3 border-t">
                      <InfoPill label="Juvonno" value={juvonnoId ? <><Badge className="bg-emerald-50 text-emerald-700 text-xs font-mono">{juvonnoId}</Badge>{juvonnoPortalUrl && <a href={`${juvonnoPortalUrl}/patient/profile.php?txtCustomerID=${juvonnoId}`} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5 text-blue-500" /></a>}</> : 'Not linked'} />
                      <InfoPill label="Type" value={
                        <span className="inline-flex items-center gap-1.5">
                          <Badge className={cn('text-xs', patientType === 'existing' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700')}>{patientType}</Badge>
                          {call?.patientTypeSource === 'deterministic_3rule' || call?.patientTypeSource === 'deterministic_3rule_backfill' ? (
                            <Badge
                              className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] font-medium gap-0.5"
                              title="Determined by the 3-rule check: full name + email + phone + Juvonno ID + booking on record."
                              data-testid="patient-type-rule-pill"
                            >
                              <Sparkles className="h-2.5 w-2.5" /> via rule
                            </Badge>
                          ) : null}
                        </span>
                      } />
                      {editFields.source && <InfoPill label="Source" value={<Badge className="bg-purple-50 text-purple-700 text-xs" data-testid="contact-source">{editFields.source}</Badge>} />}
                      <InfoPill label="Last Appointment" value={
                        appointmentsLoading
                          ? <span className="text-slate-400 text-xs">Loading...</span>
                          : lastAppointmentDate
                            ? <Badge className="bg-indigo-50 text-indigo-700 text-xs" data-testid="last-appointment-date">{format(lastAppointmentDate, 'MMM d, yyyy')}</Badge>
                            : <span className="text-slate-400 text-xs">None</span>
                      } />
                    </div>
                  </Card>
                  <Card title="Address" icon={MapPin}>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2"><Field label="Street Address" value={editFields.address1} onChange={v => setEditFields(p => ({ ...p, address1: v }))} /></div>
                      <Field label="City" value={editFields.city} onChange={v => setEditFields(p => ({ ...p, city: v }))} />
                      <Field label="Province" value={editFields.state} onChange={v => setEditFields(p => ({ ...p, state: v }))} />
                      <Field label="Postal Code" value={editFields.postalCode} onChange={v => setEditFields(p => ({ ...p, postalCode: v }))} />
                    </div>
                  </Card>
                  {contact?.tags?.length > 0 && (
                    <Card title="Tags" icon={Tag}>
                      <div className="flex flex-wrap gap-1.5">{contact.tags.map(tag => <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>)}</div>
                    </Card>
                  )}
                </div>
              )}

              {activeTab === 'fd-team' && (
                <div className="p-6">
                  <Card title="Front Desk Team Fields" icon={Users}>
                    <div className="space-y-4">
                      {FRONT_DESK_FIELDS.map(fd => {
                        const fieldDef = customFieldMap[fd.key];
                        const hasPicklist = fieldDef?.picklistOptions?.length > 0;
                        return (
                          <div key={fd.key}>
                            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">{fd.label}</label>
                            {hasPicklist ? (
                              <Select value={fdValues[fd.key] || ''} onValueChange={v => handleSaveFdField(fd.key, v === '__none__' ? '' : v)}>
                                <SelectTrigger className="h-10"><SelectValue placeholder="Select..." /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">-- None --</SelectItem>
                                  {fieldDef.picklistOptions.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="flex gap-2">
                                <Input value={fdValues[fd.key] || ''} onChange={e => setFdValues(p => ({ ...p, [fd.key]: e.target.value }))} className="h-10 flex-1" />
                                <Button variant="outline" className="h-10 px-3" onClick={() => handleSaveFdField(fd.key, fdValues[fd.key])} disabled={fdSaving}><Save className="h-4 w-4" /></Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                </div>
              )}

              {activeTab === 'notes' && (
                <div className="p-6 space-y-4">
                  {!showAddNote ? (
                    <Button onClick={() => setShowAddNote(true)} className="w-full gap-1.5" variant="outline" data-testid="show-add-note"><Plus className="h-4 w-4" /> New Note</Button>
                  ) : (
                    <Card title="Add Note">
                      <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Write your note here..."
                        className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm min-h-[120px] resize-none focus:outline-none focus:ring-2 focus:ring-slate-400/40 bg-slate-50" data-testid="add-note-input" />
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <Select value={noteAgent} onValueChange={setNoteAgent}>
                          <SelectTrigger className="h-9 w-[170px] text-xs"><SelectValue placeholder="Front Desk Agent" /></SelectTrigger>
                          <SelectContent><SelectItem value="__none__">-- No agent --</SelectItem>{agents.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={noteUserId} onValueChange={setNoteUserId}>
                          <SelectTrigger className="h-9 w-[170px] text-xs" data-testid="note-crm-user"><SelectValue placeholder="CRM User" /></SelectTrigger>
                          <SelectContent><SelectItem value="__none__">-- Default user --</SelectItem>{crmUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="flex gap-2 ml-auto">
                          <Button variant="ghost" size="sm" onClick={() => { setShowAddNote(false); setNewNote(''); }}>Cancel</Button>
                          <Button size="sm" onClick={handleAddNote} disabled={noteSaving || !newNote.trim()} className="gap-1" data-testid="add-note-btn">
                            {noteSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Add
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )}
                  {notesLoading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                  : notes.length === 0 ? <Empty text="No notes yet" />
                  : <div className="space-y-3">{notes.map(n => (
                      <div key={n.id} className="bg-white rounded-xl border p-4">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{n.body}</p>
                        <p className="text-[11px] text-slate-400 mt-3 pt-2 border-t border-slate-100">{n.dateAdded ? format(new Date(n.dateAdded), 'MMM d, yyyy h:mm a') : ''}</p>
                      </div>
                    ))}</div>}
                </div>
              )}

              {activeTab === 'tasks' && (
                <div className="p-6 space-y-4">
                  {!showAddTask ? (
                    <Button onClick={() => setShowAddTask(true)} className="w-full gap-1.5" variant="outline" data-testid="show-add-task"><Plus className="h-4 w-4" /> New Task</Button>
                  ) : (
                    <Card title="Create Task">
                      <Input value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)} placeholder="Task title..." className="h-10 mb-2" data-testid="add-task-title" />
                      <textarea value={newTaskDesc} onChange={e => setNewTaskDesc(e.target.value)} placeholder="Description (optional)..."
                        className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-slate-400/40 bg-slate-50" />
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <Select value={taskAgent} onValueChange={setTaskAgent}>
                          <SelectTrigger className="h-9 w-[170px] text-xs"><SelectValue placeholder="Front Desk Agent" /></SelectTrigger>
                          <SelectContent><SelectItem value="__none__">-- No agent --</SelectItem>{agents.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={taskUserId} onValueChange={setTaskUserId}>
                          <SelectTrigger className="h-9 w-[170px] text-xs" data-testid="task-crm-user"><SelectValue placeholder="Assign to CRM User" /></SelectTrigger>
                          <SelectContent><SelectItem value="__none__">-- Contact's assigned user --</SelectItem>{crmUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="flex gap-2 ml-auto">
                          <Button variant="ghost" size="sm" onClick={() => { setShowAddTask(false); setNewTaskTitle(''); setNewTaskDesc(''); }}>Cancel</Button>
                          <Button size="sm" onClick={handleAddTask} disabled={taskSaving || !newTaskTitle.trim()} className="gap-1" data-testid="add-task-btn">
                            {taskSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Add
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )}
                  {tasksLoading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                  : tasks.length === 0 ? <Empty text="No tasks yet" />
                  : <div className="space-y-3">{tasks.map(t => (
                      <div key={t.id} className="bg-white rounded-xl border p-4 flex items-start gap-3">
                        <CheckCircle className={cn('h-5 w-5 mt-0.5 flex-shrink-0', t.completed ? 'text-green-500' : 'text-slate-300')} />
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium', t.completed && 'line-through text-slate-400')}>{stripHtml(t.title || t.body)}</p>
                          {t.description && <p className="text-xs text-slate-500 mt-1">{stripHtml(t.description)}</p>}
                          <p className="text-[11px] text-slate-400 mt-1.5">{t.dueDate ? `Due: ${format(new Date(t.dueDate), 'MMM d')}` : ''} {t.dateAdded ? `Created: ${format(new Date(t.dateAdded), 'MMM d')}` : ''}</p>
                        </div>
                      </div>
                    ))}</div>}
                </div>
              )}

              {activeTab === 'appointments' && (
                <div className="p-6 space-y-4">
                  {/* Book Appointment Button */}
                  <Button
                    onClick={() => setShowScheduleGrid(true)}
                    className="w-full gap-2 h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm"
                    data-testid="book-appointment-from-panel"
                  >
                    <CalendarPlus className="h-4 w-4" /> Book New Appointment
                  </Button>

                  {appointmentsLoading ? <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                  : appointments.length === 0 ? <Empty text="No appointments found" icon={CalendarDays} />
                  : (() => {
                      const now = new Date();
                      const upcoming = appointments.filter(a => new Date(a.startTime || 0) >= now);
                      const past = appointments.filter(a => new Date(a.startTime || 0) < now);
                      return <>
                        {upcoming.length > 0 && <Section title="Upcoming">{upcoming.map((a, i) => <AptCard key={a.id || i} apt={a} upcoming />)}</Section>}
                        {past.length > 0 && <Section title="Past">{past.map((a, i) => <AptCard key={a.id || i} apt={a} />)}</Section>}
                      </>;
                    })()}
                </div>
              )}
            </>
          )}
        </div>

        {/* Scheduling Grid Dialog - reused from Call Tracker */}
        <SchedulingGridDialog
          open={showScheduleGrid}
          onOpenChange={(open) => {
            setShowScheduleGrid(open);
            if (!open) {
              // Refresh appointments after closing the scheduler
              setTimeout(() => loadAppointments(), 500);
            }
          }}
          locationId={locationId}
          initialContact={contact ? {
            ...contact,
            gender: editFields.gender || contact.gender || '',
            dateOfBirth: editFields.dateOfBirth || contact.dateOfBirth || '',
          } : null}
        />
      </SheetContent>
    </Sheet>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-xl border p-5">
      {title && <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5 mb-4">{Icon && <Icon className="h-4 w-4" />} {title}</h3>}
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', icon: Icon }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />}
        <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)} className={cn('h-10 bg-white', Icon && 'pl-9')} />
      </div>
    </div>
  );
}

function InfoPill({ label, value }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-slate-500">{label}:</span>
      <span className="text-xs text-slate-700 flex items-center gap-1">{value}</span>
    </div>
  );
}

function Empty({ text, icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-slate-400">
      {Icon && <Icon className="h-8 w-8 mb-2 opacity-50" />}
      <p className="text-sm">{text}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function AptCard({ apt, upcoming }) {
  const title = apt.title || apt.appointmentType || 'Appointment';
  const status = apt.appointmentStatus || 'unknown';
  return (
    <div className={cn('bg-white rounded-xl border p-4', upcoming && 'border-blue-200 bg-blue-50/30')}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-800">{title}</span>
        <Badge className={cn('text-[10px]', status === 'confirmed' ? 'bg-green-100 text-green-700' : status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600')}>{status}</Badge>
      </div>
      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
        {apt.startTime && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {format(new Date(apt.startTime), 'MMM d, yyyy h:mm a')}</span>}
        {apt.calendarName && <span className="text-slate-400">{apt.calendarName}</span>}
      </div>
    </div>
  );
}
